/**
 * Sistema de temas data-driven (inspirado en Monkeytype).
 *
 * Cada tema es un objeto con ~15 valores semilla. Un expand() calcula los
 * ~48 tokens CSS finales (aliases de accent, tints, fg, etc.).
 *
 * Añadir un tema = añadir un ThemeSeed al array SEEDS. Nada más.
 */

import type { Theme as ThemeId } from '$domain/types';
export type { ThemeId };

export interface ThemeTokens {
  'color-bg': string;
  'color-surface': string;
  'color-surface-elevated': string;
  'color-surface-sunken': string;
  'color-surface-hover': string;
  'color-border': string;
  'color-border-strong': string;
  'color-text-primary': string;
  'color-text-secondary': string;
  'color-text-muted': string;
  'color-text-faint': string;
  'color-brand': string;
  'color-brand-hover': string;
  'color-brand-fg': string;
  'color-brand-tint': string;
  'color-accent': string;
  'color-accent-hover': string;
  'color-accent-fg': string;
  'color-accent-tint': string;
  'color-focus-ring': string;
  'color-success': string;
  'color-success-fg': string;
  'color-success-tint': string;
  'color-warning': string;
  'color-warning-fg': string;
  'color-warning-tint': string;
  'color-danger': string;
  'color-danger-fg': string;
  'color-danger-tint': string;
  'color-info': string;
  'color-info-fg': string;
  'color-info-tint': string;
  'color-rating-again': string;
  'color-rating-hard': string;
  'color-rating-good': string;
  'color-rating-easy': string;
  'heat-0': string;
  'heat-1': string;
  'heat-2': string;
  'heat-3': string;
  'heat-4': string;
  'heat-border': string;
  'heat-hover-ring': string;
  'heat-today': string;
  'curve-1': string;
  'curve-2': string;
  'curve-3': string;
  'curve-4': string;
  'curve-5': string;
  'curve-6': string;
  'color-scrim': string;
  'color-scrim-soft': string;
  'color-online': string;
  'color-offline': string;
}

export interface ThemeDefinition {
  id: ThemeId;
  name: string;
  colorScheme: 'dark' | 'light';
  tokens: ThemeTokens;
}

interface ThemeSeed {
  id: ThemeId;
  name: string;
  colorScheme: 'dark' | 'light';
  bg: string;
  surface: string;
  surfaceElevated?: string;
  surfaceSunken?: string;
  surfaceHover?: string;
  border?: string;
  borderStrong?: string;
  textPrimary: string;
  textSecondary?: string;
  textMuted?: string;
  textFaint?: string;
  brand: string;
  brandHover?: string;
  brandFg?: string;
  success?: string;
  warning?: string;
  danger?: string;
  info?: string;
  heat0: string;
  heat1: string;
  heat2: string;
  heat3: string;
  heat4: string;
}

function hexToRgba(hex: string, alpha: number): string {
  if (hex.startsWith('rgba') || hex.startsWith('rgb')) return hex;
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function expand(s: ThemeSeed): ThemeDefinition {
  const isDark = s.colorScheme === 'dark';
  const brandFg = s.brandFg ?? (isDark ? s.bg : '#FFFFFF');
  const success = s.success ?? (isDark ? '#7FA88D' : '#52796F');
  const warning = s.warning ?? (isDark ? '#C77B4E' : '#C77B4E');
  const danger = s.danger ?? (isDark ? '#F87171' : '#C1121F');
  const info = s.info ?? (isDark ? '#6D72C3' : '#457B9D');
  const border = s.border ?? (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(30,30,36,0.08)');
  const borderStrong = s.borderStrong ?? (isDark ? 'rgba(255,255,255,0.16)' : 'rgba(30,30,36,0.16)');
  const textSec = s.textSecondary ?? (isDark ? hexToRgba(s.textPrimary, 0.92) : hexToRgba(s.textPrimary, 0.85));
  const textMut = s.textMuted ?? (isDark ? hexToRgba(s.textPrimary, 0.78) : hexToRgba(s.textPrimary, 0.6));
  const textFaint = s.textFaint ?? (isDark ? hexToRgba(s.textPrimary, 0.5) : hexToRgba(s.textPrimary, 0.35));

  return {
    id: s.id,
    name: s.name,
    colorScheme: s.colorScheme,
    tokens: {
      'color-bg': s.bg,
      'color-surface': s.surface,
      'color-surface-elevated': s.surfaceElevated ?? s.surface,
      'color-surface-sunken': s.surfaceSunken ?? s.bg,
      'color-surface-hover': s.surfaceHover ?? s.surface,
      'color-border': border,
      'color-border-strong': borderStrong,
      'color-text-primary': s.textPrimary,
      'color-text-secondary': textSec,
      'color-text-muted': textMut,
      'color-text-faint': textFaint,
      'color-brand': s.brand,
      'color-brand-hover': s.brandHover ?? s.brand,
      'color-brand-fg': brandFg,
      'color-brand-tint': hexToRgba(s.brand, 0.15),
      'color-accent': s.brand,
      'color-accent-hover': s.brandHover ?? s.brand,
      'color-accent-fg': brandFg,
      'color-accent-tint': hexToRgba(s.brand, 0.15),
      'color-focus-ring': s.brand,
      'color-success': success,
      'color-success-fg': isDark ? s.bg : '#FFFFFF',
      'color-success-tint': hexToRgba(success, 0.15),
      'color-warning': warning,
      'color-warning-fg': isDark ? s.bg : '#FFFFFF',
      'color-warning-tint': hexToRgba(warning, 0.15),
      'color-danger': danger,
      'color-danger-fg': isDark ? s.bg : '#FFFFFF',
      'color-danger-tint': hexToRgba(danger, 0.12),
      'color-info': info,
      'color-info-fg': isDark ? s.textPrimary : '#FFFFFF',
      'color-info-tint': hexToRgba(info, 0.15),
      'color-rating-again': danger,
      'color-rating-hard': warning,
      'color-rating-good': success,
      'color-rating-easy': info,
      'heat-0': s.heat0,
      'heat-1': s.heat1,
      'heat-2': s.heat2,
      'heat-3': s.heat3,
      'heat-4': s.heat4,
      'heat-border': isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
      'heat-hover-ring': isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.55)',
      'heat-today': s.brand,
      // Curvas FSRS: 6 colores derivados de tokens semánticos del tema.
      // Forman un gradiente intuitivo: volátil (rojo) → estable (neutro).
      // El usuario los ve como sequence natural sin hex hardcoded.
      'curve-1': danger,
      'curve-2': warning,
      'curve-3': s.brand,
      'curve-4': success,
      'curve-5': info,
      'curve-6': isDark ? hexToRgba(s.textPrimary, 0.7) : hexToRgba(s.textPrimary, 0.65),
      'color-scrim': isDark ? 'rgba(0,0,0,0.6)' : 'rgba(30,30,36,0.32)',
      'color-scrim-soft': isDark ? 'rgba(0,0,0,0.3)' : 'rgba(30,30,36,0.16)',
      'color-online': success,
      'color-offline': textFaint
    }
  };
}

const SEEDS: ThemeSeed[] = [
  // ===== Originales =====
  { id: 'dark', name: 'Dark', colorScheme: 'dark',
    bg: '#1E1E24', surface: '#26262D', surfaceElevated: '#2E2E37', surfaceSunken: '#1A1A20', surfaceHover: '#2E2E37',
    border: 'rgba(255, 248, 240, 0.08)', borderStrong: 'rgba(255, 248, 240, 0.16)',
    textPrimary: '#FFF8F0', textSecondary: 'rgba(255, 248, 240, 0.92)', textMuted: 'rgba(255, 248, 240, 0.78)', textFaint: 'rgba(255, 248, 240, 0.50)',
    brand: '#C9A96E', brandHover: '#D9B97E', brandFg: '#1E1E24',
    success: '#7FA88D', warning: '#C77B4E', danger: '#F87171', info: '#6D72C3',
    heat0: '#002500', heat1: '#7C3238', heat2: '#B7245C', heat3: '#929982', heat4: '#EDCBB1' },

  { id: 'light', name: 'Light', colorScheme: 'light',
    bg: '#F5F5F7', surface: '#FFFFFF', surfaceElevated: '#FFFFFF', surfaceSunken: '#EAEAEA', surfaceHover: '#EAEAEA',
    border: 'rgba(30, 30, 36, 0.08)', borderStrong: 'rgba(30, 30, 36, 0.16)',
    textPrimary: '#1E1E24', textSecondary: 'rgba(30, 30, 36, 0.85)', textMuted: 'rgba(30, 30, 36, 0.60)', textFaint: 'rgba(30, 30, 36, 0.35)',
    brand: '#A67C3D', brandHover: '#8A6630', brandFg: '#FFF8F0',
    success: '#52796F', warning: '#C77B4E', danger: '#C1121F', info: '#457B9D',
    heat0: '#EBEDF0', heat1: '#FF9B71', heat2: '#1B998B', heat3: '#E84855', heat4: '#2D3047' },

  { id: 'sand', name: 'Sand', colorScheme: 'light',
    bg: '#F5F3EF', surface: '#FDFBF7', surfaceElevated: '#FFFFFF', surfaceSunken: '#EDEAE3', surfaceHover: '#EAE6DC',
    border: 'rgba(101, 113, 102, 0.12)', borderStrong: 'rgba(101, 113, 102, 0.22)',
    textPrimary: '#2A302E', textSecondary: '#657166', textMuted: 'rgba(101, 113, 102, 0.62)', textFaint: 'rgba(101, 113, 102, 0.38)',
    brand: '#B06E48', brandHover: '#985A38', brandFg: '#FDFBF7',
    success: '#5D7B62', warning: '#C0754D', danger: '#B6410F', info: '#556F94',
    heat0: '#EDEAE3', heat1: '#EDCBB1', heat2: '#929982', heat3: '#B7245C', heat4: '#7C3238' },

  { id: 'teal', name: 'Teal', colorScheme: 'dark',
    bg: '#0D1D25', surface: '#15262E', surfaceElevated: '#1D333C', surfaceSunken: '#0A1519', surfaceHover: '#1D333C',
    border: 'rgba(214, 227, 230, 0.08)', borderStrong: 'rgba(214, 227, 230, 0.16)',
    textPrimary: '#E8EDEE', textSecondary: '#C6C6D0', textMuted: 'rgba(198, 198, 208, 0.62)', textFaint: 'rgba(198, 198, 208, 0.38)',
    brand: '#D59D80', brandHover: '#E0AB8E', brandFg: '#0D1D25',
    success: '#7FA88D', warning: '#C0754D', danger: '#F87171', info: '#7B93B8',
    heat0: '#2D3047', heat1: '#1B998B', heat2: '#FF9B71', heat3: '#E84855', heat4: '#FFB991' },

  { id: 'oled', name: 'OLED', colorScheme: 'dark',
    bg: '#000000', surface: '#0A0A0C', surfaceElevated: '#111115', surfaceSunken: '#000000', surfaceHover: '#111115',
    border: 'rgba(255, 255, 255, 0.12)', borderStrong: 'rgba(255, 255, 255, 0.22)',
    textPrimary: 'rgba(255, 255, 255, 0.92)', textSecondary: 'rgba(255, 255, 255, 0.78)', textMuted: 'rgba(255, 255, 255, 0.56)', textFaint: 'rgba(255, 255, 255, 0.40)',
    brand: '#E0BE7A', brandHover: '#EACF8E', brandFg: '#000000',
    success: '#7FA88D', warning: '#C77B4E', danger: '#F87171', info: '#6D72C3',
    heat0: '#002500', heat1: '#7C3238', heat2: '#B7245C', heat3: '#929982', heat4: '#EDCBB1' },

  { id: 'sage', name: 'Sage', colorScheme: 'dark',
    bg: '#0F1F0F', surface: '#173017', surfaceElevated: '#1F3D1F', surfaceSunken: '#081408', surfaceHover: '#1F3D1F',
    border: 'rgba(237, 203, 177, 0.10)', borderStrong: 'rgba(237, 203, 177, 0.20)',
    textPrimary: '#EDCBB1', textSecondary: 'rgba(237, 203, 177, 0.88)', textMuted: 'rgba(237, 203, 177, 0.68)', textFaint: 'rgba(237, 203, 177, 0.42)',
    brand: '#929982', brandHover: '#A8AF95', brandFg: '#0F1F0F',
    success: '#A8C79B', warning: '#EDCBB1', danger: '#E85A6F', info: '#7C98B3',
    heat0: '#002500', heat1: '#7C3238', heat2: '#B7245C', heat3: '#929982', heat4: '#EDCBB1' },

  { id: 'wine', name: 'Wine', colorScheme: 'dark',
    bg: '#000004', surface: '#0A0A14', surfaceElevated: '#14141E', surfaceSunken: '#000000', surfaceHover: '#14141E',
    border: 'rgba(251, 254, 249, 0.08)', borderStrong: 'rgba(251, 254, 249, 0.16)',
    textPrimary: '#FBFEF9', textSecondary: 'rgba(251, 254, 249, 0.88)', textMuted: 'rgba(251, 254, 249, 0.68)', textFaint: 'rgba(251, 254, 249, 0.42)',
    brand: '#A63446', brandHover: '#C34A5D', brandFg: '#FBFEF9',
    success: '#7FA88D', warning: '#E5A76F', danger: '#E84855', info: '#4A9BD1',
    heat0: '#0A0A14', heat1: '#7E1946', heat2: '#0C6291', heat3: '#A63446', heat4: '#FBFEF9' },

  { id: 'coral', name: 'Coral', colorScheme: 'dark',
    bg: '#2D3047', surface: '#35384F', surfaceElevated: '#3E4159', surfaceSunken: '#242638', surfaceHover: '#3E4159',
    border: 'rgba(240, 242, 245, 0.10)', borderStrong: 'rgba(240, 242, 245, 0.20)',
    textPrimary: '#F0F2F5', textSecondary: 'rgba(240, 242, 245, 0.88)', textMuted: 'rgba(240, 242, 245, 0.68)', textFaint: 'rgba(240, 242, 245, 0.42)',
    brand: '#FF9B71', brandHover: '#FFB991', brandFg: '#2D3047',
    success: '#35C2B0', warning: '#FFB991', danger: '#E84855', info: '#7B9FD1',
    heat0: '#35384F', heat1: '#1B998B', heat2: '#FF9B71', heat3: '#FFB991', heat4: '#E84855' },

  // ===== Classics =====
  { id: 'nord', name: 'Nord', colorScheme: 'dark',
    bg: '#2E3440', surface: '#3B4252', surfaceElevated: '#434C5E', surfaceSunken: '#272C36', surfaceHover: '#434C5E',
    textPrimary: '#ECEFF4', textSecondary: '#D8DEE9', textMuted: 'rgba(216, 222, 233, 0.68)', textFaint: 'rgba(216, 222, 233, 0.42)',
    brand: '#88C0D0', brandHover: '#8FBCBB', brandFg: '#2E3440',
    success: '#A3BE8C', warning: '#EBCB8B', danger: '#BF616A', info: '#81A1C1',
    heat0: '#2E3440', heat1: '#5E81AC', heat2: '#88C0D0', heat3: '#8FBCBB', heat4: '#A3BE8C' },

  { id: 'dracula', name: 'Dracula', colorScheme: 'dark',
    bg: '#282A36', surface: '#343746', surfaceElevated: '#3E4159', surfaceSunken: '#1E1F29', surfaceHover: '#3E4159',
    textPrimary: '#F8F8F2', textSecondary: '#C8CAD9', textMuted: 'rgba(200, 202, 217, 0.68)', textFaint: 'rgba(200, 202, 217, 0.42)',
    brand: '#BD93F9', brandHover: '#CBA9FA', brandFg: '#282A36',
    success: '#50FA7B', warning: '#F1FA8C', danger: '#FF5555', info: '#8BE9FD',
    heat0: '#282A36', heat1: '#6272A4', heat2: '#8BE9FD', heat3: '#50FA7B', heat4: '#BD93F9' },

  { id: 'tokyo-night', name: 'Tokyo Night', colorScheme: 'dark',
    bg: '#1A1B26', surface: '#24283B', surfaceElevated: '#2F3449', surfaceSunken: '#13141C', surfaceHover: '#2F3449',
    textPrimary: '#C0CAF5', textSecondary: '#A9B1D6', textMuted: 'rgba(169, 177, 214, 0.68)', textFaint: 'rgba(169, 177, 214, 0.42)',
    brand: '#7AA2F7', brandHover: '#8FB4FF', brandFg: '#1A1B26',
    success: '#9ECE6A', warning: '#E0AF68', danger: '#F7768E', info: '#7DCFFF',
    heat0: '#1A1B26', heat1: '#414868', heat2: '#7AA2F7', heat3: '#BB9AF7', heat4: '#7DCFFF' },

  { id: 'catppuccin-mocha', name: 'Catppuccin Mocha', colorScheme: 'dark',
    bg: '#1E1E2E', surface: '#313244', surfaceElevated: '#45475A', surfaceSunken: '#181825', surfaceHover: '#45475A',
    textPrimary: '#CDD6F4', textSecondary: '#BAC2DE', textMuted: 'rgba(186, 194, 222, 0.68)', textFaint: 'rgba(186, 194, 222, 0.42)',
    brand: '#CBA6F7', brandHover: '#D7B8FA', brandFg: '#1E1E2E',
    success: '#A6E3A1', warning: '#F9E2AF', danger: '#F38BA8', info: '#89B4FA',
    heat0: '#1E1E2E', heat1: '#585B70', heat2: '#89B4FA', heat3: '#A6E3A1', heat4: '#F5C2E7' },

  { id: 'catppuccin-latte', name: 'Catppuccin Latte', colorScheme: 'light',
    bg: '#EFF1F5', surface: '#FFFFFF', surfaceElevated: '#FFFFFF', surfaceSunken: '#E6E9EF', surfaceHover: '#E6E9EF',
    textPrimary: '#4C4F69', textSecondary: '#5C5F77', textMuted: 'rgba(92, 95, 119, 0.68)', textFaint: 'rgba(92, 95, 119, 0.42)',
    brand: '#8839EF', brandHover: '#9E5BF5', brandFg: '#FFFFFF',
    success: '#40A02B', warning: '#DF8E1D', danger: '#D20F39', info: '#1E66F5',
    heat0: '#DCE0E8', heat1: '#9CA0B0', heat2: '#1E66F5', heat3: '#40A02B', heat4: '#8839EF' },

  { id: 'gruvbox-dark', name: 'Gruvbox Dark', colorScheme: 'dark',
    bg: '#282828', surface: '#3C3836', surfaceElevated: '#504945', surfaceSunken: '#1D2021', surfaceHover: '#504945',
    textPrimary: '#EBDBB2', textSecondary: '#D5C4A1', textMuted: 'rgba(213, 196, 161, 0.68)', textFaint: 'rgba(213, 196, 161, 0.42)',
    brand: '#FABD2F', brandHover: '#FCCB4D', brandFg: '#282828',
    success: '#B8BB26', warning: '#FE8019', danger: '#FB4934', info: '#83A598',
    heat0: '#282828', heat1: '#665C54', heat2: '#D65D0E', heat3: '#B8BB26', heat4: '#FABD2F' },

  { id: 'gruvbox-light', name: 'Gruvbox Light', colorScheme: 'light',
    bg: '#FBF1C7', surface: '#F2E5BC', surfaceElevated: '#F9F5D7', surfaceSunken: '#EBDBB2', surfaceHover: '#EBDBB2',
    textPrimary: '#3C3836', textSecondary: '#504945', textMuted: 'rgba(80, 73, 69, 0.68)', textFaint: 'rgba(80, 73, 69, 0.42)',
    brand: '#B57614', brandHover: '#D79921', brandFg: '#FBF1C7',
    success: '#79740E', warning: '#AF3A03', danger: '#9D0006', info: '#076678',
    heat0: '#EBDBB2', heat1: '#D5C4A1', heat2: '#D65D0E', heat3: '#B8BB26', heat4: '#B57614' },

  { id: 'solarized-dark', name: 'Solarized Dark', colorScheme: 'dark',
    bg: '#002B36', surface: '#073642', surfaceElevated: '#0E4452', surfaceSunken: '#001F27', surfaceHover: '#0E4452',
    textPrimary: '#93A1A1', textSecondary: '#839496', textMuted: 'rgba(131, 148, 150, 0.68)', textFaint: 'rgba(131, 148, 150, 0.42)',
    brand: '#B58900', brandHover: '#D3A017', brandFg: '#002B36',
    success: '#859900', warning: '#CB4B16', danger: '#DC322F', info: '#268BD2',
    heat0: '#002B36', heat1: '#586E75', heat2: '#B58900', heat3: '#859900', heat4: '#268BD2' },

  { id: 'solarized-light', name: 'Solarized Light', colorScheme: 'light',
    bg: '#FDF6E3', surface: '#EEE8D5', surfaceElevated: '#FFFBF0', surfaceSunken: '#E4DDC8', surfaceHover: '#E4DDC8',
    textPrimary: '#586E75', textSecondary: '#657B83', textMuted: 'rgba(101, 123, 131, 0.68)', textFaint: 'rgba(101, 123, 131, 0.42)',
    brand: '#B58900', brandHover: '#CB9D17', brandFg: '#FDF6E3',
    success: '#859900', warning: '#CB4B16', danger: '#DC322F', info: '#268BD2',
    heat0: '#EEE8D5', heat1: '#93A1A1', heat2: '#B58900', heat3: '#859900', heat4: '#268BD2' },

  { id: 'one-dark', name: 'One Dark', colorScheme: 'dark',
    bg: '#282C34', surface: '#353B45', surfaceElevated: '#3E4451', surfaceSunken: '#21252B', surfaceHover: '#3E4451',
    textPrimary: '#ABB2BF', textSecondary: '#9DA5B4', textMuted: 'rgba(157, 165, 180, 0.68)', textFaint: 'rgba(157, 165, 180, 0.42)',
    brand: '#61AFEF', brandHover: '#7EC0F3', brandFg: '#282C34',
    success: '#98C379', warning: '#E5C07B', danger: '#E06C75', info: '#56B6C2',
    heat0: '#282C34', heat1: '#3E4451', heat2: '#61AFEF', heat3: '#98C379', heat4: '#E5C07B' },

  { id: 'monokai', name: 'Monokai', colorScheme: 'dark',
    bg: '#272822', surface: '#383830', surfaceElevated: '#4A4A3E', surfaceSunken: '#1E1F1A', surfaceHover: '#4A4A3E',
    textPrimary: '#F8F8F2', textSecondary: '#CFCFC2', textMuted: 'rgba(207, 207, 194, 0.68)', textFaint: 'rgba(207, 207, 194, 0.42)',
    brand: '#A6E22E', brandHover: '#B8F04A', brandFg: '#272822',
    success: '#A6E22E', warning: '#FD971F', danger: '#F92672', info: '#66D9EF',
    heat0: '#272822', heat1: '#75715E', heat2: '#F92672', heat3: '#A6E22E', heat4: '#FD971F' },

  { id: 'rose-pine', name: 'Rosé Pine', colorScheme: 'dark',
    bg: '#191724', surface: '#1F1D2E', surfaceElevated: '#26233A', surfaceSunken: '#14121C', surfaceHover: '#26233A',
    textPrimary: '#E0DEF4', textSecondary: '#C5C3DC', textMuted: 'rgba(197, 195, 220, 0.68)', textFaint: 'rgba(197, 195, 220, 0.42)',
    brand: '#EBBCBA', brandHover: '#F1CECB', brandFg: '#191724',
    success: '#9CCFD8', warning: '#F6C177', danger: '#EB6F92', info: '#31748F',
    heat0: '#191724', heat1: '#26233A', heat2: '#C4A7E7', heat3: '#EBBCBA', heat4: '#31748F' },

  { id: 'kanagawa', name: 'Kanagawa', colorScheme: 'dark',
    bg: '#1F1F28', surface: '#2A2A37', surfaceElevated: '#363646', surfaceSunken: '#16161D', surfaceHover: '#363646',
    textPrimary: '#DCD7BA', textSecondary: '#C8C093', textMuted: 'rgba(200, 192, 147, 0.68)', textFaint: 'rgba(200, 192, 147, 0.42)',
    brand: '#7E9CD8', brandHover: '#9AB0E0', brandFg: '#1F1F28',
    success: '#98BB6C', warning: '#E6C384', danger: '#E46876', info: '#7FB4CA',
    heat0: '#1F1F28', heat1: '#223249', heat2: '#7E9CD8', heat3: '#98BB6C', heat4: '#E6C384' },

  { id: 'everforest', name: 'Everforest', colorScheme: 'dark',
    bg: '#2D353B', surface: '#343F44', surfaceElevated: '#3D484D', surfaceSunken: '#232A2E', surfaceHover: '#3D484D',
    textPrimary: '#D3C6AA', textSecondary: '#B8AD97', textMuted: 'rgba(184, 173, 151, 0.68)', textFaint: 'rgba(184, 173, 151, 0.42)',
    brand: '#A7C080', brandHover: '#BAD29A', brandFg: '#2D353B',
    success: '#A7C080', warning: '#DBBC7F', danger: '#E67E80', info: '#7FBBB3',
    heat0: '#2D353B', heat1: '#475258', heat2: '#A7C080', heat3: '#DBBC7F', heat4: '#E67E80' },

  { id: 'ayu-dark', name: 'Ayu Dark', colorScheme: 'dark',
    bg: '#0A0E14', surface: '#11161D', surfaceElevated: '#182029', surfaceSunken: '#050709', surfaceHover: '#182029',
    textPrimary: '#B3B1AD', textSecondary: '#9C9B98', textMuted: 'rgba(156, 155, 152, 0.68)', textFaint: 'rgba(156, 155, 152, 0.42)',
    brand: '#E6B450', brandHover: '#F0C568', brandFg: '#0A0E14',
    success: '#7FD962', warning: '#FF8F40', danger: '#F07178', info: '#59C2FF',
    heat0: '#0A0E14', heat1: '#1F2430', heat2: '#E6B450', heat3: '#7FD962', heat4: '#FF8F40' },

  { id: 'github-dark', name: 'GitHub Dark', colorScheme: 'dark',
    bg: '#0D1117', surface: '#161B22', surfaceElevated: '#1F252E', surfaceSunken: '#010409', surfaceHover: '#1F252E',
    textPrimary: '#C9D1D9', textSecondary: '#8B949E', textMuted: 'rgba(139, 148, 158, 0.68)', textFaint: 'rgba(139, 148, 158, 0.42)',
    brand: '#58A6FF', brandHover: '#79B8FF', brandFg: '#0D1117',
    success: '#3FB950', warning: '#D29922', danger: '#F85149', info: '#58A6FF',
    heat0: '#0D1117', heat1: '#0E4429', heat2: '#26A641', heat3: '#39D353', heat4: '#56D364' },

  { id: 'github-light', name: 'GitHub Light', colorScheme: 'light',
    bg: '#FFFFFF', surface: '#F6F8FA', surfaceElevated: '#FFFFFF', surfaceSunken: '#EAEEF2', surfaceHover: '#EAEEF2',
    textPrimary: '#24292F', textSecondary: '#57606A', textMuted: 'rgba(87, 96, 106, 0.68)', textFaint: 'rgba(87, 96, 106, 0.42)',
    brand: '#0969DA', brandHover: '#0860CA', brandFg: '#FFFFFF',
    success: '#1A7F37', warning: '#9A6700', danger: '#CF222E', info: '#0969DA',
    heat0: '#EBEDF0', heat1: '#9BE9A8', heat2: '#40C463', heat3: '#30A14E', heat4: '#216E39' },


  { id: 'night-owl', name: 'Night Owl', colorScheme: 'dark',
    bg: '#011627', surface: '#0B2942', surfaceElevated: '#153A57', surfaceSunken: '#010E1A', surfaceHover: '#153A57',
    textPrimary: '#D6DEEB', textSecondary: '#B8BFCC', textMuted: 'rgba(184, 191, 204, 0.68)', textFaint: 'rgba(184, 191, 204, 0.42)',
    brand: '#82AAFF', brandHover: '#9CC0FF', brandFg: '#011627',
    success: '#7FDBCA', warning: '#ECC48D', danger: '#EF5350', info: '#82AAFF',
    heat0: '#011627', heat1: '#1D3B53', heat2: '#82AAFF', heat3: '#7FDBCA', heat4: '#C792EA' },

  { id: 'palenight', name: 'Palenight', colorScheme: 'dark',
    bg: '#292D3E', surface: '#34324A', surfaceElevated: '#3F3C55', surfaceSunken: '#1E2132', surfaceHover: '#3F3C55',
    textPrimary: '#A6ACCD', textSecondary: '#8D93B8', textMuted: 'rgba(141, 147, 184, 0.68)', textFaint: 'rgba(141, 147, 184, 0.42)',
    brand: '#C792EA', brandHover: '#D5ACEE', brandFg: '#292D3E',
    success: '#C3E88D', warning: '#FFCB6B', danger: '#F07178', info: '#82AAFF',
    heat0: '#292D3E', heat1: '#444267', heat2: '#C792EA', heat3: '#82AAFF', heat4: '#89DDFF' },

  { id: 'synthwave', name: 'Synthwave', colorScheme: 'dark',
    bg: '#241B2F', surface: '#2A2139', surfaceElevated: '#3A2D4D', surfaceSunken: '#1A1322', surfaceHover: '#3A2D4D',
    textPrimary: '#F92AAD', textSecondary: '#E4B8D5', textMuted: 'rgba(228, 184, 213, 0.68)', textFaint: 'rgba(228, 184, 213, 0.42)',
    brand: '#FF7EDB', brandHover: '#FF9EE4', brandFg: '#241B2F',
    success: '#72F1B8', warning: '#FFCC5C', danger: '#F92AAD', info: '#03EDF9',
    heat0: '#241B2F', heat1: '#6C3A8A', heat2: '#F92AAD', heat3: '#FF7EDB', heat4: '#72F1B8' },

  { id: 'vaporwave', name: 'Vaporwave', colorScheme: 'dark',
    bg: '#1A0F2E', surface: '#2A1B47', surfaceElevated: '#3A2860', surfaceSunken: '#100821', surfaceHover: '#3A2860',
    textPrimary: '#FFB3C6', textSecondary: '#E8A0B8', textMuted: 'rgba(232, 160, 184, 0.68)', textFaint: 'rgba(232, 160, 184, 0.42)',
    brand: '#FF6EC7', brandHover: '#FF8FD4', brandFg: '#1A0F2E',
    success: '#72F1B8', warning: '#FFD319', danger: '#FF3864', info: '#01CDFE',
    heat0: '#1A0F2E', heat1: '#4A2A7E', heat2: '#FF6EC7', heat3: '#72F1B8', heat4: '#FFD319' },

  { id: 'nord-light', name: 'Nord Light', colorScheme: 'light',
    bg: '#ECEFF4', surface: '#FFFFFF', surfaceElevated: '#FFFFFF', surfaceSunken: '#E5E9F0', surfaceHover: '#E5E9F0',
    textPrimary: '#2E3440', textSecondary: '#4C566A', textMuted: 'rgba(76, 86, 106, 0.68)', textFaint: 'rgba(76, 86, 106, 0.42)',
    brand: '#5E81AC', brandHover: '#4C6B93', brandFg: '#FFFFFF',
    success: '#A3BE8C', warning: '#D08770', danger: '#BF616A', info: '#81A1C1',
    heat0: '#E5E9F0', heat1: '#D8DEE9', heat2: '#81A1C1', heat3: '#5E81AC', heat4: '#4C566A' },


  { id: 'bushido', name: 'Bushido', colorScheme: 'dark',
    bg: '#242933', surface: '#2E3846', surfaceElevated: '#3A4556', surfaceSunken: '#1A1E26', surfaceHover: '#3A4556',
    textPrimary: '#F5F5F5', textSecondary: '#D0D0D0', textMuted: 'rgba(208, 208, 208, 0.68)', textFaint: 'rgba(208, 208, 208, 0.42)',
    brand: '#E63946', brandHover: '#EE5966', brandFg: '#FFFFFF',
    success: '#A3BE8C', warning: '#E5C07B', danger: '#E63946', info: '#81A1C1',
    heat0: '#242933', heat1: '#4F5A6A', heat2: '#8B90A1', heat3: '#C0C4CC', heat4: '#F5F5F5' },

  { id: 'cyberpunk', name: 'Cyberpunk', colorScheme: 'dark',
    bg: '#0D0221', surface: '#1A0F2E', surfaceElevated: '#261542', surfaceSunken: '#050110', surfaceHover: '#261542',
    textPrimary: '#FFD319', textSecondary: '#E8BE1A', textMuted: 'rgba(232, 190, 26, 0.68)', textFaint: 'rgba(232, 190, 26, 0.42)',
    brand: '#FF003C', brandHover: '#FF2A5C', brandFg: '#0D0221',
    success: '#72F1B8', warning: '#FFD319', danger: '#FF003C', info: '#03EDF9',
    heat0: '#0D0221', heat1: '#3F1059', heat2: '#FF003C', heat3: '#FFD319', heat4: '#72F1B8' },

  { id: 'paper', name: 'Paper', colorScheme: 'light',
    bg: '#EEEEEE', surface: '#FFFFFF', surfaceElevated: '#FFFFFF', surfaceSunken: '#E2E2E2', surfaceHover: '#E2E2E2',
    textPrimary: '#444444', textSecondary: '#666666', textMuted: 'rgba(102, 102, 102, 0.68)', textFaint: 'rgba(102, 102, 102, 0.42)',
    brand: '#444444', brandHover: '#666666', brandFg: '#FFFFFF',
    success: '#5D7B62', warning: '#C0754D', danger: '#B6410F', info: '#556F94',
    heat0: '#EEEEEE', heat1: '#D6D6D6', heat2: '#BABABA', heat3: '#8A8A8A', heat4: '#444444' },


  { id: 'serika', name: 'Serika', colorScheme: 'dark',
    bg: '#323437', surface: '#40424A', surfaceElevated: '#4C4F58', surfaceSunken: '#25272A', surfaceHover: '#4C4F58',
    textPrimary: '#D1D0C5', textSecondary: '#B8B7AC', textMuted: 'rgba(184, 183, 172, 0.68)', textFaint: 'rgba(184, 183, 172, 0.42)',
    brand: '#E2B714', brandHover: '#EEC534', brandFg: '#323437',
    success: '#A3BE8C', warning: '#E2B714', danger: '#CA4754', info: '#81A1C1',
    heat0: '#323437', heat1: '#646669', heat2: '#E2B714', heat3: '#E2B714', heat4: '#D1D0C5' },


  { id: 'coffee', name: 'Coffee', colorScheme: 'dark',
    bg: '#1E1611', surface: '#2B211C', surfaceElevated: '#3A2D26', surfaceSunken: '#130E0B', surfaceHover: '#3A2D26',
    textPrimary: '#E5D8C5', textSecondary: '#CBBEAB', textMuted: 'rgba(203, 190, 171, 0.68)', textFaint: 'rgba(203, 190, 171, 0.42)',
    brand: '#C8A26E', brandHover: '#D5B384', brandFg: '#1E1611',
    success: '#A3BE8C', warning: '#C8A26E', danger: '#C1624A', info: '#81A1C1',
    heat0: '#1E1611', heat1: '#4A382D', heat2: '#8B6F4E', heat3: '#C8A26E', heat4: '#E5D8C5' },


  { id: 'horizon', name: 'Horizon', colorScheme: 'dark',
    bg: '#1C1E26', surface: '#232530', surfaceElevated: '#2B2E3B', surfaceSunken: '#12141A', surfaceHover: '#2B2E3B',
    textPrimary: '#D5D8DA', textSecondary: '#BBBFBF', textMuted: 'rgba(187, 191, 191, 0.68)', textFaint: 'rgba(187, 191, 191, 0.42)',
    brand: '#E95678', brandHover: '#EE768E', brandFg: '#1C1E26',
    success: '#29D398', warning: '#FAB795', danger: '#E95678', info: '#59C2FF',
    heat0: '#1C1E26', heat1: '#2E303E', heat2: '#E95678', heat3: '#FAB795', heat4: '#FAC29A' },

  { id: 'matcha', name: 'Matcha', colorScheme: 'light',
    bg: '#F5F7F0', surface: '#FFFFFF', surfaceElevated: '#FFFFFF', surfaceSunken: '#EAF0DF', surfaceHover: '#EAF0DF',
    textPrimary: '#2C3E1F', textSecondary: '#4E5A44', textMuted: 'rgba(78, 90, 68, 0.68)', textFaint: 'rgba(78, 90, 68, 0.42)',
    brand: '#5B8C3E', brandHover: '#74A651', brandFg: '#FFFFFF',
    success: '#5B8C3E', warning: '#C89B3C', danger: '#C1413A', info: '#2C6680',
    heat0: '#EAF0DF', heat1: '#C5D7AB', heat2: '#9CC46E', heat3: '#5B8C3E', heat4: '#3D6B27' },

  { id: 'sakura', name: 'Sakura', colorScheme: 'light',
    bg: '#FFF5F7', surface: '#FFFFFF', surfaceElevated: '#FFFFFF', surfaceSunken: '#FFE5EC', surfaceHover: '#FFE5EC',
    textPrimary: '#4A2A3A', textSecondary: '#7A5A6A', textMuted: 'rgba(122, 90, 106, 0.68)', textFaint: 'rgba(122, 90, 106, 0.42)',
    brand: '#E87A9E', brandHover: '#EC92AF', brandFg: '#FFFFFF',
    success: '#5B8C3E', warning: '#C89B3C', danger: '#C1413A', info: '#2C6680',
    heat0: '#FFE5EC', heat1: '#FFC0CB', heat2: '#E87A9E', heat3: '#C44A7A', heat4: '#8A2C57' },

  { id: 'mizu', name: 'Mizu', colorScheme: 'light',
    bg: '#EBF4FA', surface: '#FFFFFF', surfaceElevated: '#FFFFFF', surfaceSunken: '#D6E6F0', surfaceHover: '#D6E6F0',
    textPrimary: '#1F3A4D', textSecondary: '#3A5A70', textMuted: 'rgba(58, 90, 112, 0.68)', textFaint: 'rgba(58, 90, 112, 0.42)',
    brand: '#2E86AB', brandHover: '#3B9DC4', brandFg: '#FFFFFF',
    success: '#5B8C3E', warning: '#C89B3C', danger: '#C1413A', info: '#2E86AB',
    heat0: '#D6E6F0', heat1: '#A8C8DC', heat2: '#61A3C4', heat3: '#2E86AB', heat4: '#1E5C7A' },

  { id: 'retro', name: 'Retro', colorScheme: 'light',
    bg: '#F5E6C8', surface: '#FDF6E3', surfaceElevated: '#FFFFFF', surfaceSunken: '#E5D5B0', surfaceHover: '#E5D5B0',
    textPrimary: '#3C3836', textSecondary: '#5C5540', textMuted: 'rgba(92, 85, 64, 0.68)', textFaint: 'rgba(92, 85, 64, 0.42)',
    brand: '#8B7355', brandHover: '#A08768', brandFg: '#FDF6E3',
    success: '#5D7B62', warning: '#C0754D', danger: '#B6410F', info: '#556F94',
    heat0: '#F5E6C8', heat1: '#D5C4A1', heat2: '#A89984', heat3: '#8B7355', heat4: '#3C3836' },

  { id: 'terrazzo', name: 'Terrazzo', colorScheme: 'dark',
    bg: '#3A2E2B', surface: '#4A3B37', surfaceElevated: '#5A4842', surfaceSunken: '#2A211F', surfaceHover: '#5A4842',
    textPrimary: '#F0E5DF', textSecondary: '#D0C0B7', textMuted: 'rgba(208, 192, 183, 0.68)', textFaint: 'rgba(208, 192, 183, 0.42)',
    brand: '#D97F56', brandHover: '#E29A78', brandFg: '#3A2E2B',
    success: '#7FA88D', warning: '#D97F56', danger: '#C1513B', info: '#7B93B8',
    heat0: '#3A2E2B', heat1: '#7A5D50', heat2: '#B88570', heat3: '#D97F56', heat4: '#F0E5D9' },

  // --- NUEVOS TEMAS (Paletas de Monkeytype / iTerm2) ---

  { id: 'arch', name: 'Arch', colorScheme: 'dark',
    bg: '#0c0d11', surface: '#171a25', surfaceElevated: '#1f232f', surfaceSunken: '#08090c', surfaceHover: '#1f232f',
    textPrimary: '#f6f5f5', textSecondary: '#d0d0d0', textMuted: 'rgba(208, 208, 208, 0.68)', textFaint: 'rgba(208, 208, 208, 0.42)',
    brand: '#7ebab5', brandHover: '#9ccac6', brandFg: '#0c0d11',
    success: '#a3be8c', warning: '#e5c07b', danger: '#ff4754', info: '#81a1c1',
    heat0: '#0c0d11', heat1: '#2e313f', heat2: '#7ebab5', heat3: '#89b8c2', heat4: '#b4be82' },

  { id: 'bento', name: 'Bento', colorScheme: 'dark',
    bg: '#2d394d', surface: '#263041', surfaceElevated: '#334156', surfaceSunken: '#1f2735', surfaceHover: '#334156',
    textPrimary: '#fffaf8', textSecondary: '#d0d8e8', textMuted: 'rgba(208, 216, 232, 0.68)', textFaint: 'rgba(208, 216, 232, 0.42)',
    brand: '#ff7a90', brandHover: '#ff9aab', brandFg: '#2d394d',
    success: '#a3be8c', warning: '#e5c07b', danger: '#ee2a3a', info: '#81a1c1',
    heat0: '#2d394d', heat1: '#4a768d', heat2: '#ff7a90', heat3: '#c3e88d', heat4: '#ffcb6b' },

  { id: 'bliss', name: 'Bliss', colorScheme: 'dark',
    bg: '#262727', surface: '#343231', surfaceElevated: '#3e3b3a', surfaceSunken: '#1d1c1b', surfaceHover: '#3e3b3a',
    textPrimary: '#fff', textSecondary: '#d0d0d0', textMuted: 'rgba(208, 208, 208, 0.68)', textFaint: 'rgba(208, 208, 208, 0.42)',
    brand: '#f0d3c9', brandHover: '#f5e0d9', brandFg: '#262727',
    success: '#a3be8c', warning: '#e5c07b', danger: '#bd4141', info: '#81a1c1',
    heat0: '#262727', heat1: '#665957', heat2: '#f0d3c9', heat3: '#c3e88d', heat4: '#ffcb6b' },

  { id: 'botanical', name: 'Botanical', colorScheme: 'light',
    bg: '#7b9c98', surface: '#72908d', surfaceElevated: '#88a8a5', surfaceSunken: '#6b8885', surfaceHover: '#88a8a5',
    textPrimary: '#eaf1f3', textSecondary: '#c8d8d5', textMuted: 'rgba(200, 216, 213, 0.68)', textFaint: 'rgba(200, 216, 213, 0.42)',
    brand: '#eaf1f3', brandHover: '#ffffff', brandFg: '#7b9c98',
    success: '#a3be8c', warning: '#f6c9b4', danger: '#f59a71', info: '#81a1c1',
    heat0: '#7b9c98', heat1: '#495755', heat2: '#eaf1f3', heat3: '#c3e88d', heat4: '#ffcb6b' },

  { id: 'carbon', name: 'Carbon', colorScheme: 'dark',
    bg: '#313131', surface: '#2b2b2b', surfaceElevated: '#3a3a3a', surfaceSunken: '#252525', surfaceHover: '#3a3a3a',
    textPrimary: '#f5e6c8', textSecondary: '#d0c0a0', textMuted: 'rgba(208, 192, 160, 0.68)', textFaint: 'rgba(208, 192, 160, 0.42)',
    brand: '#f66e0d', brandHover: '#f88b3d', brandFg: '#313131',
    success: '#a3be8c', warning: '#e5c07b', danger: '#e72d2d', info: '#81a1c1',
    heat0: '#313131', heat1: '#616161', heat2: '#f66e0d', heat3: '#c3e88d', heat4: '#ffcb6b' },

  { id: 'serika-light', name: 'Serika Light', colorScheme: 'light',
    bg: '#e1e1e3', surface: '#d1d3d8', surfaceElevated: '#e8e8ea', surfaceSunken: '#c8c8cc', surfaceHover: '#e8e8ea',
    textPrimary: '#323437', textSecondary: '#5c5f64', textMuted: 'rgba(92, 95, 100, 0.68)', textFaint: 'rgba(92, 95, 100, 0.42)',
    brand: '#d1a510', brandHover: '#e6b81a', brandFg: '#e1e1e3',
    success: '#5d7b62', warning: '#c0754d', danger: '#b6410f', info: '#556f94',
    heat0: '#e1e1e3', heat1: '#d6d6d6', heat2: '#d1a510', heat3: '#5d7b62', heat4: '#c0754d' },

  { id: 'miami-nights', name: 'Miami Nights', colorScheme: 'dark',
    bg: '#18181a', surface: '#0f0f10', surfaceElevated: '#222224', surfaceSunken: '#0a0a0b', surfaceHover: '#222224',
    textPrimary: '#fff', textSecondary: '#d0d0d0', textMuted: 'rgba(208, 208, 208, 0.68)', textFaint: 'rgba(208, 208, 208, 0.42)',
    brand: '#e4609b', brandHover: '#ea7fb0', brandFg: '#18181a',
    success: '#72f1b8', warning: '#fff591', danger: '#b6af68', info: '#03edf9',
    heat0: '#18181a', heat1: '#47bac0', heat2: '#e4609b', heat3: '#72f1b8', heat4: '#fff591' },

  { id: 'terra', name: 'Terra', colorScheme: 'dark',
    bg: '#0c100e', surface: '#0f1d18', surfaceElevated: '#152a22', surfaceSunken: '#080a09', surfaceHover: '#152a22',
    textPrimary: '#f0edd1', textSecondary: '#d0cba8', textMuted: 'rgba(208, 203, 168, 0.68)', textFaint: 'rgba(208, 203, 168, 0.42)',
    brand: '#89c559', brandHover: '#a3d67a', brandFg: '#0c100e',
    success: '#89c559', warning: '#d3ca78', danger: '#89844d', info: '#7fbbb3',
    heat0: '#0c100e', heat1: '#436029', heat2: '#89c559', heat3: '#d3ca78', heat4: '#f0edd1' },

  { id: '9009', name: '9009', colorScheme: 'light',
    bg: '#f5f5f5', surface: '#ffffff', surfaceElevated: '#ffffff', surfaceSunken: '#e8e8e8', surfaceHover: '#e8e8e8',
    textPrimary: '#2e2e2e', textSecondary: '#5a5a5a', textMuted: 'rgba(90, 90, 90, 0.68)', textFaint: 'rgba(90, 90, 90, 0.42)',
    brand: '#00a86b', brandHover: '#00c77f', brandFg: '#ffffff',
    success: '#00a86b', warning: '#d1a510', danger: '#c1121f', info: '#457b9d',
    heat0: '#e8e8e8', heat1: '#d6d6d6', heat2: '#00a86b', heat3: '#5d7b62', heat4: '#c0754d' },

  { id: 'aether', name: 'Aether', colorScheme: 'dark',
    bg: '#1a1a2e', surface: '#16213e', surfaceElevated: '#1f2b4d', surfaceSunken: '#0f0f1a', surfaceHover: '#1f2b4d',
    textPrimary: '#e2e2e2', textSecondary: '#b0b0c0', textMuted: 'rgba(176, 176, 192, 0.68)', textFaint: 'rgba(176, 176, 192, 0.42)',
    brand: '#e94560', brandHover: '#f06b7f', brandFg: '#ffffff',
    success: '#4ecca3', warning: '#f9d56e', danger: '#e94560', info: '#6d72c3',
    heat0: '#1a1a2e', heat1: '#2e313f', heat2: '#e94560', heat3: '#4ecca3', heat4: '#f9d56e' },

  { id: 'aurora', name: 'Aurora', colorScheme: 'dark',
    bg: '#0f172a', surface: '#1e293b', surfaceElevated: '#273449', surfaceSunken: '#0a101f', surfaceHover: '#273449',
    textPrimary: '#e2e8f0', textSecondary: '#cbd5e1', textMuted: 'rgba(203, 213, 225, 0.68)', textFaint: 'rgba(203, 213, 225, 0.42)',
    brand: '#38bdf8', brandHover: '#7dd3fc', brandFg: '#0f172a',
    success: '#4ade80', warning: '#fbbf24', danger: '#f87171', info: '#38bdf8',
    heat0: '#0f172a', heat1: '#334155', heat2: '#38bdf8', heat3: '#4ade80', heat4: '#fbbf24' },

  { id: 'beach', name: 'Beach', colorScheme: 'light',
    bg: '#fef3c7', surface: '#fffbeb', surfaceElevated: '#ffffff', surfaceSunken: '#fde68a', surfaceHover: '#fde68a',
    textPrimary: '#1e293b', textSecondary: '#475569', textMuted: 'rgba(71, 85, 105, 0.68)', textFaint: 'rgba(71, 85, 105, 0.42)',
    brand: '#0ea5e9', brandHover: '#38bdf8', brandFg: '#ffffff',
    success: '#10b981', warning: '#f59e0b', danger: '#ef4444', info: '#3b82f6',
    heat0: '#fde68a', heat1: '#fcd34d', heat2: '#0ea5e9', heat3: '#10b981', heat4: '#f59e0b' },

  { id: 'bingsu', name: 'Bingsu', colorScheme: 'light',
    bg: '#fdf2f8', surface: '#ffffff', surfaceElevated: '#ffffff', surfaceSunken: '#fce7f3', surfaceHover: '#fce7f3',
    textPrimary: '#1f2937', textSecondary: '#4b5563', textMuted: 'rgba(75, 85, 99, 0.68)', textFaint: 'rgba(75, 85, 99, 0.42)',
    brand: '#ec4899', brandHover: '#f472b6', brandFg: '#ffffff',
    success: '#10b981', warning: '#f59e0b', danger: '#ef4444', info: '#8b5cf6',
    heat0: '#fce7f3', heat1: '#fbcfe8', heat2: '#ec4899', heat3: '#8b5cf6', heat4: '#f59e0b' },

  { id: 'blueberry-dark', name: 'Blueberry Dark', colorScheme: 'dark',
    bg: '#1e1e2e', surface: '#181825', surfaceElevated: '#252536', surfaceSunken: '#11111b', surfaceHover: '#252536',
    textPrimary: '#cdd6f4', textSecondary: '#bac2de', textMuted: 'rgba(186, 194, 222, 0.68)', textFaint: 'rgba(186, 194, 222, 0.42)',
    brand: '#89b4fa', brandHover: '#a6c8ff', brandFg: '#1e1e2e',
    success: '#a6e3a1', warning: '#f9e2af', danger: '#f38ba8', info: '#89b4fa',
    heat0: '#1e1e2e', heat1: '#585b70', heat2: '#89b4fa', heat3: '#a6e3a1', heat4: '#f5c2e7' },

  { id: 'blueberry-light', name: 'Blueberry Light', colorScheme: 'light',
    bg: '#eff1f5', surface: '#ffffff', surfaceElevated: '#ffffff', surfaceSunken: '#e6e9ef', surfaceHover: '#e6e9ef',
    textPrimary: '#4c4f69', textSecondary: '#5c5f77', textMuted: 'rgba(92, 95, 119, 0.68)', textFaint: 'rgba(92, 95, 119, 0.42)',
    brand: '#1e66f5', brandHover: '#4a8aff', brandFg: '#ffffff',
    success: '#40a02b', warning: '#df8e1d', danger: '#d20f39', info: '#1e66f5',
    heat0: '#e6e9ef', heat1: '#dce0e8', heat2: '#1e66f5', heat3: '#40a02b', heat4: '#8839ef' },

  { id: 'camping', name: 'Camping', colorScheme: 'dark',
    bg: '#1c2b1c', surface: '#162216', surfaceElevated: '#243824', surfaceSunken: '#121a12', surfaceHover: '#243824',
    textPrimary: '#e5e9f0', textSecondary: '#c0c8d0', textMuted: 'rgba(192, 200, 208, 0.68)', textFaint: 'rgba(192, 200, 208, 0.42)',
    brand: '#a3be8c', brandHover: '#b8d1a3', brandFg: '#1c2b1c',
    success: '#a3be8c', warning: '#ebcb8b', danger: '#bf616a', info: '#81a1c1',
    heat0: '#1c2b1c', heat1: '#3b4a3b', heat2: '#a3be8c', heat3: '#ebcb8b', heat4: '#bf616a' },

  { id: 'chaos-theory', name: 'Chaos Theory', colorScheme: 'dark',
    bg: '#1a1a1a', surface: '#262626', surfaceElevated: '#333333', surfaceSunken: '#111111', surfaceHover: '#333333',
    textPrimary: '#f8f9fa', textSecondary: '#d0d0d0', textMuted: 'rgba(208, 208, 208, 0.68)', textFaint: 'rgba(208, 208, 208, 0.42)',
    brand: '#ff6b6b', brandHover: '#ff8e8e', brandFg: '#1a1a1a',
    success: '#51cf66', warning: '#fcc419', danger: '#ff6b6b', info: '#339af0',
    heat0: '#1a1a1a', heat1: '#444444', heat2: '#ff6b6b', heat3: '#51cf66', heat4: '#fcc419' },

  { id: 'cheesecake', name: 'Cheesecake', colorScheme: 'light',
    bg: '#fff8e7', surface: '#ffffff', surfaceElevated: '#ffffff', surfaceSunken: '#fef0d0', surfaceHover: '#fef0d0',
    textPrimary: '#3e2723', textSecondary: '#6d4c41', textMuted: 'rgba(109, 76, 65, 0.68)', textFaint: 'rgba(109, 76, 65, 0.42)',
    brand: '#d4a373', brandHover: '#e0b88a', brandFg: '#3e2723',
    success: '#5d7b62', warning: '#c89b3c', danger: '#c1413a', info: '#556f94',
    heat0: '#fef0d0', heat1: '#fde2b3', heat2: '#d4a373', heat3: '#5d7b62', heat4: '#c89b3c' },

  { id: 'dark-note', name: 'Dark Note', colorScheme: 'dark',
    bg: '#1e1e1e', surface: '#2a2a2a', surfaceElevated: '#363636', surfaceSunken: '#141414', surfaceHover: '#363636',
    textPrimary: '#e5e5e5', textSecondary: '#c0c0c0', textMuted: 'rgba(192, 192, 192, 0.68)', textFaint: 'rgba(192, 192, 192, 0.42)',
    brand: '#fbbf24', brandHover: '#fcd34d', brandFg: '#1e1e1e',
    success: '#4ade80', warning: '#fbbf24', danger: '#f87171', info: '#60a5fa',
    heat0: '#1e1e1e', heat1: '#444444', heat2: '#fbbf24', heat3: '#4ade80', heat4: '#f87171' }
];

export const THEMES: readonly ThemeDefinition[] = SEEDS.map(expand);

export const THEME_IDS: readonly ThemeId[] = THEMES.map((t) => t.id);

const DEFAULT_THEME_ID: ThemeId = 'dark';
const THEME_MAP: Map<ThemeId, ThemeDefinition> = new Map(THEMES.map((t) => [t.id, t]));

export function getThemeById(id: ThemeId): ThemeDefinition {
  return THEME_MAP.get(id) ?? THEME_MAP.get(DEFAULT_THEME_ID)!;
}

export const THEME_TOKEN_KEYS: readonly (keyof ThemeTokens)[] = Object.keys(
  getThemeById(DEFAULT_THEME_ID).tokens
) as (keyof ThemeTokens)[];